import { useLayoutEffect, useState, type RefObject } from 'react';

export interface PopoverAnchor {
  readonly top: number;
  readonly left: number;
}

const VIEWPORT_MARGIN_PX = 8;

// #516 -- shared by every toolbar-anchored popover that needs to escape an ancestor's
// overflow-clipping (position: fixed, coordinates computed from the trigger's own
// boundingClientRect -- see FeedbackDisclosure.tsx's #480 comment for why `position: absolute`
// doesn't work here). Right-aligns the popover to the trigger by default (its right edge lines
// up with the trigger's right edge, growing leftward/downward), then clamps so it can never
// overflow either viewport edge: a trigger doesn't have to sit at the viewport's absolute right
// edge for a wide-enough popover to still push past the *left* one on a narrow viewport (#516 --
// FeedbackDisclosure's panel did exactly this on a phone; #512 hit the identical issue for
// MoreMenu once its trigger could end up anywhere along a scrollable toolbar).
//
// `estimateWidth` supplies the popover's width before it's first measured (the panel doesn't
// exist in the DOM -- and so can't be measured -- until `anchor` is first set); pass whatever
// formula matches the popover's own CSS width. Once mounted, `panelRef`'s actual rendered width
// is used instead, so the estimate only has to be right for the first frame.
export function useAnchoredPopover(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  estimateWidth: () => number
): PopoverAnchor | null {
  const [anchor, setAnchor] = useState<PopoverAnchor | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const updateAnchor = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = panelRef.current?.getBoundingClientRect().width ?? estimateWidth();
      const maxLeft = Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - width - VIEWPORT_MARGIN_PX);
      const left = Math.min(Math.max(rect.right - width, VIEWPORT_MARGIN_PX), maxLeft);
      setAnchor({ top: rect.bottom + 6, left });
    };
    updateAnchor();
    // Capture phase: scroll events don't bubble, but a capture-phase listener on `document` still
    // sees a scroll fired by any ancestor scroll container, not just `window` itself.
    document.addEventListener('scroll', updateAnchor, true);
    window.addEventListener('resize', updateAnchor);
    return () => {
      document.removeEventListener('scroll', updateAnchor, true);
      window.removeEventListener('resize', updateAnchor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerRef/panelRef are stable refs; estimateWidth is a per-render closure that's fine to skip re-running the effect for.
  }, [open]);

  return anchor;
}
